import { z } from "zod/v4";
import { type QueryRowType, type Transaction } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import { type Context, zql } from "../../zero/schema";
import { MealPlan } from "../meal-plan";
import { defineAgent } from "../../ai/agent-definition/agent";
import { defineStep, defineWorkflow } from "../../ai/define-workflow";
import { FoodItemMatcher, FoodItemResolver } from "../food-matcher";
import {
  type InferUITools,
  type LanguageModelResponseMetadata,
  type LanguageModelUsage,
  type UIDataTypes,
  type UIMessage,
} from "ai";
import type { ModelCallSettings } from "../../ai/model-call-settings";
import type { Food } from "../food";
import { Nutrient } from "../nutrient";
import { NutritionProgram } from "../program";
import { typedEntries } from "../../utils/typed-object";
import {
  createId,
  must,
  safeParseInt,
  type FormSchemaType,
  zFormInt,
  zFormTime,
  zTimeHHmm,
} from "@corpuscomplete/core/utils";
import { AskQuestionsTool } from "../../ai/agent/ask-questions";
import { projectTool } from "../../ai/agent/tool-lifecycle";

/**
 * Meal-plan agent/workflow boundary for AI-assisted plan generation.
 *
 * Invariants:
 * - Generation context is derived from one consistent nutrition-program day.
 * - Item matching keeps explicit unmatched/ambiguous states for safe workflows.
 *
 * @see `packages/core/docs/ai/agents.md`
 */
export namespace MealPlanProcessing {
  // TODO: Should "ingredients" be allowed to be recipes themselves?
  export const mealDescriptorSchema = z.object({
    slotName: z
      .string()
      .describe("The name of the meal slot, e.g. 'Breakfast', 'Lunch', 'Dinner', 'Snack'"),
    name: z
      .string()
      .describe(
        "The name of the meal, e.g. 'Chicken Stir Fry', 'Baked Salmon and Asparagus', 'Pasta Carbonara'",
      ),
    ingredients: z.array(FoodItemMatcher.ItemDescriptor).describe("The ingredients for the meal"),
  });
  export type MealDescriptor = z.infer<typeof mealDescriptorSchema>;

  export const mealDescriptorWithItemIdsSchema = z.object({
    ...mealDescriptorSchema.shape,
    ingredients: z.array(FoodItemMatcher.ItemDescriptor.extend({ id: z.uuid() })),
  });
  export type MealDescriptorWithItemIds = z.infer<typeof mealDescriptorWithItemIdsSchema>;

  export type ItemDescriptorWithMatch = {
    itemDescriptor: FoodItemMatcher.ItemDescriptor & { id: string };
    matchResult: FoodItemMatcher.FoodItemMatchResult;
  };

  export type ItemMatchResult = {
    matchedItems: Array<Food.Item & { id: string }>;
    anyUnmatchedItems: boolean;
    tokenUsage: {
      data: LanguageModelUsage;
      responseMetadata: LanguageModelResponseMetadata;
    } | null;
  };

  export async function matchItemsForMeal(
    meal: MealDescriptor,
    itemDescriptorsWithMatches: ItemDescriptorWithMatch[],
    settings: ModelCallSettings,
  ): Promise<ItemMatchResult> {
    const unambiguousItems: ItemMatchResult["matchedItems"] = [];
    const ambiguousItems: Array<{
      descriptor: FoodItemMatcher.ItemDescriptor;
      matchResult: FoodItemMatcher.FoodItemMatchResult;
      id: string;
    }> = [];

    for (const { itemDescriptor, matchResult } of itemDescriptorsWithMatches) {
      if (matchResult?.type === "foodAndServing") {
        unambiguousItems.push({
          id: itemDescriptor.id,
          amount: itemDescriptor.quantity,
          food: matchResult.food,
          serving: matchResult.serving,
        });
      } else if (matchResult?.type === "foodOnly" || matchResult?.type === "ambiguous") {
        ambiguousItems.push({
          descriptor: itemDescriptor,
          matchResult,
          id: itemDescriptor.id,
        });
      }
    }

    if (ambiguousItems.length === 0) {
      return { matchedItems: unambiguousItems, anyUnmatchedItems: false, tokenUsage: null };
    }

    const disambiguationResult = await FoodItemResolver.resolveAmbiguousItems(
      {
        context: meal.name,
        items: ambiguousItems.map(({ descriptor, matchResult }) => ({
          descriptor,
          matchResult,
        })),
      },
      settings,
    );

    const matchedItems = [...unambiguousItems];
    let resolvedCount = 0;

    for (const result of disambiguationResult) {
      if (!result.hasMatch || !result.food || !result.serving) {
        continue;
      }

      const ambiguousItem = ambiguousItems[result.itemIndex];
      if (!ambiguousItem) {
        console.error(`Invalid itemIndex ${result.itemIndex} in disambiguation result`);
        continue;
      }

      resolvedCount++;
      matchedItems.push({
        id: ambiguousItem.id,
        amount: result.suggestedQuantity ?? ambiguousItem.descriptor.quantity,
        food: result.food,
        serving: result.serving,
      });
    }

    return {
      matchedItems,
      anyUnmatchedItems: resolvedCount < ambiguousItems.length,
      tokenUsage: null,
    };
  }
}

export namespace MealVerification {
  const issueSchema = z.discriminatedUnion("code", [
    z.object({
      code: z.literal("unresolved_descriptors"),
      descriptorIds: z.array(z.uuid()).min(1),
    }),
    z.object({
      code: z.literal("nutrient_target_miss"),
      failedKeys: z.array(Nutrient.keySchema),
    }),
  ]);

  export const resultSchema = z.discriminatedUnion("status", [
    z.object({
      status: z.literal("passed"),
    }),
    z.object({
      status: z.literal("skipped"),
      reason: z.literal("missing_targets"),
    }),
    z.object({
      status: z.literal("failed"),
      issue: issueSchema,
    }),
  ]);
  export type Result = z.infer<typeof resultSchema>;

  const defaultAbsoluteErrorByKey: Partial<Record<Nutrient.Key, number>> = {
    energy: 50,
    protein: 5,
    carbohydrate: 5,
    fat: 3,
  };

  function verifyTargets(args: { measured: Nutrient.Amounts; targets: Nutrient.Targets }): Result {
    const check = Nutrient.checkAmounts(args.measured, args.targets, {
      defaultTargetOptions: { relativeError: 0.1 },
      targetOptionsByKey: Object.fromEntries(
        typedEntries(defaultAbsoluteErrorByKey).map(([key, absoluteError]) => [
          key,
          { absoluteError, relativeError: 0.1 },
        ]),
      ) as Partial<Record<Nutrient.Key, { absoluteError?: number; relativeError?: number }>>,
    });

    if (check.ok) {
      return {
        status: "passed",
      };
    }

    return {
      status: "failed",
      issue: {
        code: "nutrient_target_miss",
        failedKeys: check.failedKeys,
      },
    };
  }

  export function verifyMeal(args: {
    items: readonly Food.Item[];
    itemDescriptors: ReadonlyArray<Pick<MealPlan.MealItemDescriptor, "id">>;
    targets: Nutrient.Targets | null;
  }): Result {
    if (args.itemDescriptors.length > 0) {
      return {
        status: "failed",
        issue: {
          code: "unresolved_descriptors",
          descriptorIds: args.itemDescriptors.map((descriptor) => descriptor.id),
        },
      };
    }

    if (!args.targets || Object.keys(args.targets).length === 0) {
      return {
        status: "skipped",
        reason: "missing_targets",
      };
    }

    const measured = Nutrient.addFoodItems(Nutrient.emptyAmounts(), args.items);
    return verifyTargets({ measured, targets: args.targets });
  }
}

/**
 * Meal-plan agent orchestration namespace.
 *
 * Invariants:
 * - Agent availability requires nutrition context for the requested date range.
 * - Computed nutrition context uses one stable day profile across the span.
 *
 * @see `packages/core/docs/ai/agents.md`
 */
export namespace MealPlanAgent {
  export const nutritionContextSchema = z.object({
    dailyTargets: Nutrient.targetsSchema(),
    goalSummary: z.string().nullable(),
  });
  export type NutritionContext = z.infer<typeof nutritionContextSchema>;

  export type UnavailableReason = "no_program" | "no_program_for_dates" | "inconsistent_days";

  export function getUnavailabilityReason(params: {
    programs: NutritionProgram.Preview[];
    dateStart: string;
    dateEnd: string;
  }): UnavailableReason | null {
    const { programs, dateStart, dateEnd } = params;

    if (programs.length === 0) {
      return "no_program";
    }

    const start = Temporal.PlainDate.from(dateStart);
    const end = Temporal.PlainDate.from(dateEnd);
    const dates: Temporal.PlainDate[] = [];
    let current = start;
    while (Temporal.PlainDate.compare(current, end) <= 0) {
      dates.push(current);
      current = current.add({ days: 1 });
    }

    const days = dates.map((date) => NutritionProgram.dayForDate(programs, date));

    if (days.some((day) => day === undefined)) {
      return "no_program_for_dates";
    }

    const firstDayId = must(days[0]).id;
    const allSameDay = days.every((day) => must(day).id === firstDayId);
    if (!allSameDay) {
      return "inconsistent_days";
    }

    return null;
  }

  export function computeNutritionContext(params: {
    programs: NutritionProgram.Preview[];
    programInfo: { expenditure?: { goalOffset: number } | null } | null | undefined;
    dateStart: string;
    dateEnd: string;
  }): NutritionContext | null {
    const { programs, programInfo, dateStart, dateEnd } = params;

    if (programs.length === 0) {
      return null;
    }

    const start = Temporal.PlainDate.from(dateStart);
    const end = Temporal.PlainDate.from(dateEnd);
    const dates: Temporal.PlainDate[] = [];
    let current = start;
    while (Temporal.PlainDate.compare(current, end) <= 0) {
      dates.push(current);
      current = current.add({ days: 1 });
    }

    const days = dates.map((date) => NutritionProgram.dayForDate(programs, date));

    if (days.some((day) => day === undefined)) {
      return null;
    }

    const firstDayId = must(days[0]).id;
    const allSameDay = days.every((day) => must(day).id === firstDayId);
    if (!allSameDay) {
      return null;
    }

    const day = must(days[0]);
    const dailyTargets = day.nutrientTargets as Nutrient.Targets;

    let goalSummary: string | null = null;
    if (programInfo?.expenditure) {
      const { goalOffset } = programInfo.expenditure;
      goalSummary = NutritionProgram.formatGoalSummary(goalOffset);
    }

    return {
      dailyTargets,
      goalSummary,
    };
  }

  export async function getNutritionContext(
    tx: Transaction,
    ctx: Context,
    params: {
      userId: string;
      dateStart: string;
      dateEnd: string;
    },
  ): Promise<NutritionContext | null> {
    const { userId, dateStart, dateEnd } = params;
    const dateRange = {
      dateStart: Temporal.PlainDate.from(dateStart),
      dateEnd: Temporal.PlainDate.from(dateEnd),
    };

    const programs = await tx.run(
      NutritionProgram.inDateRange.fn({
        ctx,
        args: { dateRange, userId },
      }),
    );

    if (programs.length === 0) {
      return null;
    }

    const programInfo = await tx.run(
      NutritionProgram.onDate.fn({
        ctx,
        args: { userId, date: dateStart },
      }),
    );

    return computeNutritionContext({
      programs,
      programInfo: programInfo ?? null,
      dateStart,
      dateEnd,
    });
  }

  const planDraftMealSchema = z.object({
    name: z
      .string()
      .min(1)
      .describe("Meal name for this slot, e.g. Greek yogurt bowl or Turkey sandwich"),
    occurrenceCount: z
      .number()
      .int()
      .positive()
      .default(1)
      .describe(
        "How many times this meal should repeat in the plan. Use 1 if it should appear once.",
      ),
  });

  function nutrientTargetsSchemaForContext(context: NutritionContext | null) {
    if (context) {
      return Nutrient.targetsSchema().describe(
        'Required per-slot nutrient targets. Because personalized nutrition context is available, every slot must include nutrientTargets. Supported target shapes are exact {"type":"exact","value":500}, between {"type":"between","min":25,"max":40}, atLeast {"type":"atLeast","value":30}, and atMost {"type":"atMost","value":20}. Do not use max for nutrient targets. Example: {"energy":{"type":"exact","value":500},"protein":{"type":"atLeast","value":35},"fat":{"type":"atMost","value":20}}',
      );
    }

    return z.null().describe("Always use null.");
  }

  const optionalNutrientTargetsSchema = Nutrient.targetsSchema()
    .nullable()
    .optional()
    .default(null)
    .describe(
      'Per-slot nutrient targets, or null when you are not assigning targets for this slot. Supported target shapes are exact, between, atLeast, and atMost; do not use max for nutrient targets. Example: {"energy":{"type":"exact","value":500},"protein":{"type":"between","min":30,"max":45},"fat":{"type":"atMost","value":20}}',
    );

  const nullableNutrientTargetsSchema = Nutrient.targetsSchema().nullable();

  export type PlanFormNutrientTargetsMode = "optional" | "required" | "disabled";

  function planFormNutrientTargetsSchema(
    type: FormSchemaType,
    nutrientTargetsMode: PlanFormNutrientTargetsMode,
  ) {
    const schema = Nutrient.macroEnergyTargetsFormSchema(type);
    if (nutrientTargetsMode === "optional") {
      return schema;
    }

    return schema.check((ctx) => {
      const value = ctx.value;
      if (nutrientTargetsMode === "required") {
        const hasAnyTarget = [value.energy, value.protein, value.carbohydrate, value.fat].some(
          (entry) => typeof entry === "string" && entry.trim().length > 0,
        );

        if (!hasAnyTarget) {
          ctx.issues.push({
            code: "custom",
            message: "At least one nutrient target is required",
            path: ["energy"],
            input: value,
          });
        }
        return;
      }

      if (value.enabled) {
        ctx.issues.push({
          code: "custom",
          message: "Nutrient targets are unavailable",
          path: ["enabled"],
          input: value.enabled,
        });
      }
    });
  }

  function planDraftSlotSchemaForContext(context: NutritionContext | null) {
    return z.object({
      name: z.string().min(1).describe("Slot name, e.g. Breakfast, Lunch, Dinner, Snack"),
      time: zTimeHHmm().describe(
        "Required time for this slot in HH:mm format, e.g. 08:00, 12:30, or 18:30.",
      ),
      nutrientTargets: nutrientTargetsSchemaForContext(context),
      meals: z
        .array(planDraftMealSchema)
        .min(1)
        .describe(
          "Meals proposed for this slot. Each meal must include a name and meal-level occurrenceCount.",
        ),
    });
  }

  function planSlotSchemaForContext(context: NutritionContext | null) {
    return z.object({
      slotId: z.string().min(1),
      name: z.string().min(1),
      time: zTimeHHmm().describe(
        "Required time for this slot in HH:mm format, e.g. 08:00, 12:30, or 18:30.",
      ),
      nutrientTargets: nutrientTargetsSchemaForContext(context),
      meals: z.array(
        z.object({
          mealId: z.string().min(1),
          name: z.string().min(1),
          occurrenceIds: z.array(z.string().min(1)),
        }),
      ),
    });
  }

  const planDraftSlotSchema = z.object({
    name: z.string().min(1).describe("Slot name, e.g. Breakfast, Lunch, Dinner, Snack"),
    time: zTimeHHmm().describe(
      "Required time for this slot in HH:mm format, e.g. 08:00, 12:30, or 18:30.",
    ),
    nutrientTargets: optionalNutrientTargetsSchema,
    meals: z
      .array(planDraftMealSchema)
      .min(1)
      .describe(
        "Meals proposed for this slot. Each meal must include a name and meal-level occurrenceCount.",
      ),
  });

  const planSlotSchema = z.object({
    slotId: z.string().min(1),
    name: z.string().min(1),
    time: zTimeHHmm().describe(
      "Required time for this slot in HH:mm format, e.g. 08:00, 12:30, or 18:30.",
    ),
    nutrientTargets: nullableNutrientTargetsSchema,
    meals: z.array(
      z.object({
        mealId: z.string().min(1),
        name: z.string().min(1),
        occurrenceIds: z.array(z.string().min(1)),
      }),
    ),
  });

  export const PlanDraftSchema = z.object({
    slots: z
      .array(planDraftSlotSchema)
      .min(1)
      .describe(
        'Ordered meal slots for the plan. Example: [{"name":"Breakfast","time":"08:00","nutrientTargets":null,"meals":[{"name":"Greek yogurt bowl","occurrenceCount":2}]}]',
      ),
  });

  export const PlanSchema = z.object({
    slots: z.array(planSlotSchema),
  });

  export const planFormSchema = (
    type: FormSchemaType,
    nutrientTargetsMode: PlanFormNutrientTargetsMode = "optional",
  ) =>
    z.object({
      slots: z
        .array(
          z.object({
            name: z.string().min(type === "validation" ? 1 : 0, {
              message: "Slot name is required",
            }),
            time: zFormTime(type),
            nutrientTargets: planFormNutrientTargetsSchema(type, nutrientTargetsMode),
            meals: z
              .array(
                z.object({
                  name: z.string().min(type === "validation" ? 1 : 0, {
                    message: "Meal name is required",
                  }),
                  occurrenceCount: zFormInt(type),
                }),
              )
              .min(type === "validation" ? 1 : 0, {
                message: "At least one meal is required",
              }),
          }),
        )
        .min(type === "validation" ? 1 : 0, {
          message: "At least one slot is required",
        }),
    });

  export type PlanDraft = z.infer<typeof PlanDraftSchema>;
  export type Plan = z.infer<typeof PlanSchema>;
  export type PlanForm = z.input<ReturnType<typeof planFormSchema>>;

  export function planDraftToForm(
    draft: PlanDraft,
    nutrientTargetsMode: PlanFormNutrientTargetsMode = "optional",
  ): PlanForm {
    return {
      slots: draft.slots.map((slot) => ({
        name: slot.name,
        time: slot.time,
        nutrientTargets:
          nutrientTargetsMode === "disabled"
            ? Nutrient.targetsToMacroEnergyTargetsForm(null)
            : {
                ...Nutrient.targetsToMacroEnergyTargetsForm(slot.nutrientTargets),
                enabled: nutrientTargetsMode === "required" ? true : slot.nutrientTargets != null,
              },
        meals: slot.meals.map((meal) => ({
          name: meal.name,
          occurrenceCount: meal.occurrenceCount.toString(),
        })),
      })),
    };
  }

  export function planFormToDraft(
    form: PlanForm,
    nutrientTargetsMode: PlanFormNutrientTargetsMode = "optional",
  ): PlanDraft {
    return {
      slots: form.slots.map((slot) => ({
        name: slot.name.trim(),
        time: slot.time,
        nutrientTargets:
          nutrientTargetsMode === "disabled"
            ? null
            : Nutrient.macroEnergyTargetsFormToTargets({
                ...slot.nutrientTargets,
                enabled: nutrientTargetsMode === "required" ? true : slot.nutrientTargets.enabled,
              }),
        meals: slot.meals.map((meal) => ({
          name: meal.name.trim(),
          occurrenceCount: safeParseInt(meal.occurrenceCount) ?? 1,
        })),
      })),
    };
  }

  export function planFromDraft(draft: PlanDraft): Plan {
    return {
      slots: draft.slots.map((slot) => ({
        slotId: createId(),
        name: slot.name,
        time: slot.time,
        nutrientTargets: slot.nutrientTargets,
        meals: slot.meals.map((meal) => ({
          mealId: createId(),
          name: meal.name,
          occurrenceIds: Array.from({ length: meal.occurrenceCount }, () => createId()),
        })),
      })),
    };
  }

  const PROPOSE_PLAN_TOOL_DESCRIPTION =
    "Propose a plan to generate a meal plan for the user. Only call this tool once per message.";

  function planDraftSlotsDescriptionForContext(context: NutritionContext | null) {
    return context
      ? 'Ordered meal slots for the plan. Every slot must include nutrientTargets because personalized daily targets are available. Nutrient targets support exact, between, atLeast, and atMost shapes; never use max. Example: [{"name":"Breakfast","time":"08:00","nutrientTargets":{"energy":{"type":"exact","value":500},"protein":{"type":"atLeast","value":30},"fat":{"type":"atMost","value":18}},"meals":[{"name":"Greek yogurt bowl","occurrenceCount":2}]}]'
      : 'Ordered meal slots for the plan. nutrientTargets must be null for every slot because no nutrition context is available. Example: [{"name":"Breakfast","time":"08:00","nutrientTargets":null,"meals":[{"name":"Greek yogurt bowl","occurrenceCount":2}]}]';
  }

  function proposePlanToolForContext(context: NutritionContext | null) {
    const planDraftSchema = z.object({
      slots: z
        .array(planDraftSlotSchemaForContext(context))
        .min(1)
        .describe(planDraftSlotsDescriptionForContext(context)),
    });
    const planSchema = z.object({
      slots: z.array(planSlotSchemaForContext(context)),
    });

    return projectTool({
      description: PROPOSE_PLAN_TOOL_DESCRIPTION,
      inputSchema: planDraftSchema,
      outputSchema: planSchema,
      lifecycle: { continueAfterTool: false },
    });
  }

  export const planningTools = {
    proposePlan: projectTool({
      description: PROPOSE_PLAN_TOOL_DESCRIPTION,
      inputSchema: PlanDraftSchema,
      outputSchema: PlanSchema,
      lifecycle: { continueAfterTool: false },
    }),
    askQuestions: AskQuestionsTool.define(
      "Ask the user questions to clarify their relevant preferences. Only call this tool once per message. When allowCustom is true, do NOT include 'Other' or similar options in the options array - the UI will automatically add a custom input option.",
    ),
  };

  export function planningToolsForContext(context: NutritionContext | null) {
    return {
      proposePlan: proposePlanToolForContext(context),
      askQuestions: planningTools.askQuestions,
    };
  }

  export type PlanningUIMessage = UIMessage<
    unknown,
    UIDataTypes,
    InferUITools<typeof planningTools>
  >;

  export const paramsSchema = z.object({
    mealPlanId: z.uuid(),
    userId: z.uuid(),
    initialMessageId: z.uuid(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const agentContextSchema = nutritionContextSchema.nullable();
  export type AgentContext = z.infer<typeof agentContextSchema>;

  export const definition = defineAgent({
    agentType: "meal-plan",
    version: 1,
    canCreate: async ({ tx, ctx, args }) => {
      const result = await MealPlan.canEdit(tx, ctx, args.params.mealPlanId);
      if (!result.value) {
        console.error("MealPlan.canEdit returned false");
        return false;
      }

      const mealPlan = result.mealPlan;

      if (mealPlan.ownerId !== args.params.userId) {
        console.error(`MealPlan.ownerId does not match provided userId`);
        return false;
      }

      return true;
    },
    canEdit: async ({ tx, ctx, args }) => {
      const mealPlanAgentSession = await tx.run(
        zql.mealPlanAgentSession.where("id", "=", args.sessionId).one(),
      );
      if (!mealPlanAgentSession) {
        return false;
      }
      const canEditResult = await MealPlan.canEdit(tx, ctx, mealPlanAgentSession.mealPlanId);
      return canEditResult.value;
    },
    viewable: ({ exists }, actor) =>
      exists("mealPlanAgentSession", (mpas) =>
        mpas.whereExists("mealPlan", (mp) => mp.where((e) => MealPlan.viewable(e, actor))),
      ),
    chat: {
      tools: planningTools,
      resolveTools: ({ context }) => planningToolsForContext(context),
    },
    paramsSchema,
    context: {
      schema: agentContextSchema,
      resolve: async ({ tx, ctx, args: { params } }) => {
        const mealPlan = await tx.run(MealPlan.byId.fn({ ctx, args: { id: params.mealPlanId } }));
        if (!mealPlan) return null;
        return getNutritionContext(tx, ctx, {
          userId: params.userId,
          dateStart: mealPlan.dateStart.toString(),
          dateEnd: mealPlan.dateEnd.toString(),
        });
      },
    },
  }).onCreate(async ({ tx, args: { sessionId, params } }) => {
    const timeNow = Temporal.Now.instant();
    await tx.mutate.mealPlanAgentSession.insert({
      id: sessionId,
      mealPlanId: params.mealPlanId,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
    await tx.mutate.agentSessionMessage.insert({
      id: params.initialMessageId,
      timeCreated: timeNow,
      timeUpdated: timeNow,
      role: "assistant",
      status: "streaming",
      sessionId: sessionId,
      parts: [],
    });
    return {
      initialMessageId: params.initialMessageId,
      userId: params.userId,
    };
  });

  export const sessionByMealPlanId = defineQuery(
    z.object({ mealPlanId: z.uuid() }),
    ({ args: { mealPlanId }, ctx }) =>
      zql.mealPlanAgentSession
        .where("mealPlanId", "=", mealPlanId)
        .whereExists("mealPlan", (q) => q.where((e) => MealPlan.viewable(e, ctx.actor)))
        .related("base")
        .related("mealWorkflowExecutions", (q) =>
          q.related("workflowExecution", (workflow) =>
            workflow.related("steps", (step) => step.orderBy("sortIndex", "asc")),
          ),
        )
        .one(),
  );

  export type SessionByMealPlanId = NonNullable<QueryRowType<typeof sessionByMealPlanId>>;

  export const getSessionById = (tx: Transaction, sessionId: string) => {
    return tx.run(zql.mealPlanAgentSession.where("id", "=", sessionId).one());
  };

  export function groupMealWorkflowExecutionsByMealId(session: SessionByMealPlanId) {
    const grouped = new Map<string, Array<SessionByMealPlanId["mealWorkflowExecutions"][number]>>();
    for (const execution of session.mealWorkflowExecutions) {
      const existing = grouped.get(execution.mealId);
      if (existing) {
        existing.push(execution);
      } else {
        grouped.set(execution.mealId, [execution]);
      }
    }
    return grouped;
  }

  export const mealWorkflowsBySessionId = defineQuery(
    z.object({ sessionId: z.uuid() }),
    ({ args: { sessionId }, ctx }) =>
      zql.mealPlanAgentSession
        .where("id", "=", sessionId)
        .whereExists("mealPlan", (q) => q.where((e) => MealPlan.viewable(e, ctx.actor)))
        .related("mealWorkflowExecutions", (q) =>
          q.related("workflowExecution", (workflow) =>
            workflow.related("steps", (step) => step.orderBy("sortIndex", "asc")),
          ),
        )
        .one(),
  );

  export namespace ProcessMealWorkflow {
    export const contextSchema = z.object({
      mealPlanId: z.uuid(),
      mealId: z.uuid(),
      agentSessionId: z.uuid(),
    });
    export type Context = z.infer<typeof contextSchema>;

    export const GenerateMealStepInputSchema = z.object({});
    export type GenerateMealInput = z.infer<typeof GenerateMealStepInputSchema>;

    const generateMealOutputSchema = z.object({
      mealPlanId: z.string().min(1),
      mealId: z.string().min(1),
      generated: z.literal(true),
    });
    export type GenerateMealOutput = z.infer<typeof generateMealOutputSchema>;

    export const generateMealStep = defineStep({
      name: "generateMeal",
      inputSchema: GenerateMealStepInputSchema,
      outputSchema: generateMealOutputSchema,
    });

    export const ResolveItemsStepInputSchema = z.object({});
    export type ResolveItemsInput = z.infer<typeof ResolveItemsStepInputSchema>;

    const resolveItemsOutputSchema = z.object({
      verification: MealVerification.resultSchema,
    });
    export type ResolveItemsOutput = z.infer<typeof resolveItemsOutputSchema>;

    export const resolveItemsStep = defineStep({
      name: "resolveItems",
      inputSchema: ResolveItemsStepInputSchema,
      outputSchema: resolveItemsOutputSchema,
    });

    export const AdjustItemsForTargetsStepInputSchema = z.object({});
    export type AdjustItemsForTargetsInput = z.infer<typeof AdjustItemsForTargetsStepInputSchema>;

    const adjustItemsForTargetsOutputSchema = z.object({
      verification: MealVerification.resultSchema,
    });
    export type AdjustItemsForTargetsOutput = z.infer<typeof adjustItemsForTargetsOutputSchema>;

    export const adjustItemsForTargetsStep = defineStep({
      name: "adjustItemsForTargets",
      inputSchema: AdjustItemsForTargetsStepInputSchema,
      outputSchema: adjustItemsForTargetsOutputSchema,
    });

    export const definition = defineWorkflow({
      workflowType: "MealPlanProcessMeal",
      version: 1,
      contextSchema,
      steps: {
        generateMeal: generateMealStep,
        resolveItems: resolveItemsStep,
        adjustItemsForTargets: adjustItemsForTargetsStep,
      },
      initialStep: "generateMeal",
      transitions: {
        generateMeal: (_output: GenerateMealOutput) => ({
          to: "resolveItems",
          input: {},
        }),
        resolveItems: (output: ResolveItemsOutput) =>
          output.verification.status === "failed" &&
          output.verification.issue.code === "nutrient_target_miss"
            ? {
                to: "adjustItemsForTargets",
                input: {},
              }
            : null,
        adjustItemsForTargets: null,
      },
      canCreate: async ({ tx, ctx, args }) => {
        const result = await MealPlan.canEdit(tx, ctx, args.context.mealPlanId);
        if (!result.value) return false;
        return MealPlan.allMeals(result.mealPlan).some((meal) => meal.id === args.context.mealId);
      },
      canEdit: async ({ tx, ctx, args }) => {
        const execution = await tx.run(
          zql.workflowExecution.where("id", "=", args.executionId).one(),
        );
        if (!execution) return false;
        const context = execution.context as Context;
        const result = await MealPlan.canEdit(tx, ctx, context.mealPlanId);
        return result.value;
      },
      viewable: ({ exists }, actor) =>
        exists("mealWorkflowExecutions", (mealWorkflowExecutions) =>
          mealWorkflowExecutions.whereExists("mealPlan", (mealPlan) =>
            mealPlan.where((mealPlanExpression) => MealPlan.viewable(mealPlanExpression, actor)),
          ),
        ),
    }).onError(async ({ execution, error }) => {
      console.error(
        `[MealPlanProcessMealWorkflow] Workflow ${execution.id} failed:`,
        error.message,
        error.code ? `(${error.code})` : "",
      );
    });
  }
}
